/**
 * React Flow Workflow Wrapper Component
 *
 * Wrapper for React Flow to visualize approval chains and workflows
 * Features:
 * - Approval chain visualization
 * - Decision tree flows
 * - Conditional paths
 * - Step status highlighting
 */

import React, { useCallback, useMemo } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  NodeTypes,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, Badge, Text, Group, Stack } from '@mantine/core';
import { IconCheck, IconClock, IconX } from '@tabler/icons-react';

export type StepStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'SKIPPED';

// Step status constants (matches StepStatus type)
const StepStatusValue = {
  PENDING: 'PENDING' as StepStatus,
  IN_PROGRESS: 'IN_PROGRESS' as StepStatus,
  COMPLETED: 'COMPLETED' as StepStatus,
  REJECTED: 'REJECTED' as StepStatus,
  SKIPPED: 'SKIPPED' as StepStatus,
} as const;

export interface WorkflowStep {
  id: string;
  name: string;
  description?: string;
  status: StepStatus;
  assignedTo?: string;
  completedAt?: string;
}

export interface ReactFlowWorkflowProps {
  steps: WorkflowStep[];
  onStepClick?: (stepId: string) => void;
  showMiniMap?: boolean;
  height?: string;
}

// Custom Step Node Component
const StepNode: React.FC<any> = ({ data }) => {
  const statusColors: Record<StepStatus, string> = {
    PENDING: 'gray',
    IN_PROGRESS: 'blue',
    COMPLETED: 'green',
    REJECTED: 'red',
    SKIPPED: 'gray',
  };

  const statusIcons: Record<StepStatus, React.ReactNode> = {
    PENDING: <IconClock size={16} />,
    IN_PROGRESS: <IconClock size={16} />,
    COMPLETED: <IconCheck size={16} />,
    REJECTED: <IconX size={16} />,
    SKIPPED: <IconCheck size={16} />,
  };

  return (
    <Card
      withBorder
      padding="md"
      style={{
        width: '200px',
        cursor: 'pointer',
        borderColor: statusColors[data.status as StepStatus],
        backgroundColor: data.status === StepStatusValue.IN_PROGRESS ? 'rgba(59, 130, 246, 0.1)' : 'white',
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600} size="sm">
            {data.name}
          </Text>
          <Badge color={statusColors[data.status as StepStatus]} size="sm">
            {statusIcons[data.status as StepStatus]}
          </Badge>
        </Group>
        {data.description && <Text size="xs" c="dimmed">{data.description}</Text>}
        {data.assignedTo && <Text size="xs">Assigned: {data.assignedTo}</Text>}
        {data.completedAt && (
          <Text size="xs" c="green">
            Completed: {new Date(data.completedAt).toLocaleDateString()}
          </Text>
        )}
      </Stack>
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
    </Card>
  );
};

const nodeTypes: NodeTypes = {
  stepNode: StepNode,
};

export const ReactFlowWorkflow: React.FC<ReactFlowWorkflowProps> = ({
  steps,
  onStepClick,
  showMiniMap = true,
  height = '500px',
}) => {
  // Create nodes from steps
  const nodes: Node[] = useMemo(() => {
    return steps.map((step, index) => ({
      id: step.id,
      data: {
        name: step.name,
        description: step.description,
        status: step.status,
        assignedTo: step.assignedTo,
        completedAt: step.completedAt,
      },
      position: { x: 250, y: index * 150 },
      type: 'stepNode',
    }));
  }, [steps]);

  // Create edges between consecutive steps
  const edges: Edge[] = useMemo(() => {
    return steps.slice(0, -1).map((_: any, index) => ({
      id: `edge-${index}`,
      // @ts-expect-error Possibly undefined
      source: steps[index].id,
      // @ts-expect-error Possibly undefined
      target: steps[index + 1].id,
      // @ts-expect-error Possibly undefined
      animated: steps[index].status === StepStatusValue.IN_PROGRESS,
      style: {
        // @ts-expect-error Possibly undefined
        stroke: steps[index].status === StepStatusValue.COMPLETED ? '#22c55e' : '#9ca3af',
      },
    }));
  }, [steps]);

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      onStepClick?.(node.id);
    },
    [onStepClick]
  );

  return (
    <div style={{ width: '100%', height }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        fitView
      >
        <Background />
        <Controls />
        {showMiniMap && <MiniMap />}
      </ReactFlow>
    </div>
  );
};

export default ReactFlowWorkflow;
